import { noop } from "lodash";
import { useEffect, useState } from "react";

import { shouldPreloadMobilePages } from "@/react/components/Mobile/utils/shouldPreloadMobilePages";

// Longest we wait for an idle period; it usually fires much sooner.
const IDLE_TIMEOUT_MS = 5000;
// If `requestIdleCallback` is missing there is no idle signal, so
// just wait a short while after `load`
const FALLBACK_DELAY_MS = 2000;

/**
 * `true` once the page has loaded and the browser is idle, so a router
 * `Link` can switch to `preload="render"` without competing with the initial
 * load. Stays `false` on slow or data-saver connections.
 */
export const useIdleRenderPreload = (): boolean => {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    if (!shouldPreloadMobilePages()) return undefined;

    const onIdle = () => setIsIdle(true);
    let cancelIdle = noop;
    const start = () => {
      if (typeof window.requestIdleCallback === "function") {
        const id = window.requestIdleCallback(onIdle, {
          timeout: IDLE_TIMEOUT_MS,
        });
        cancelIdle = () => window.cancelIdleCallback(id);
      } else {
        const id = window.setTimeout(onIdle, FALLBACK_DELAY_MS);
        cancelIdle = () => window.clearTimeout(id);
      }
    };

    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });

    return () => {
      window.removeEventListener("load", start);
      cancelIdle();
    };
  }, []);

  return isIdle;
};
